import { forwardRef } from 'react';
import {
  ListSubheader as MuiListSubheader,
  ListSubheaderProps as MuiListSubheaderProps,
} from '@mui/material';

/**
 * ListSubheader component - Section header for lists.
 *
 * This is a thin wrapper around Material-UI's ListSubheader component that provides:
 * - Section header styling
 * - Sticky positioning support
 * - Color variants (default, primary, inherit)
 * - Inset support for alignment with indented items
 * - Theme integration
 *
 * Common use cases:
 * - Section headers in navigation lists
 * - Category headers in grouped lists
 * - Work task section headers
 * - Settings category labels
 *
 * @example
 * // Basic subheader
 * <List subheader={<ListSubheader>Section Title</ListSubheader>}>
 *   <ListItem>Item 1</ListItem>
 *   <ListItem>Item 2</ListItem>
 * </List>
 *
 * @example
 * // Primary color subheader
 * <List subheader={<ListSubheader color="primary">Important Section</ListSubheader>}>
 *   <ListItem>Item 1</ListItem>
 * </List>
 *
 * @example
 * // Inset subheader (aligned with indented items)
 * <List>
 *   <ListSubheader inset>Nested Section</ListSubheader>
 *   <ListItem sx={{ pl: 4 }}>Nested Item</ListItem>
 * </List>
 */
export type ListSubheaderProps = MuiListSubheaderProps;

export const ListSubheader = forwardRef<HTMLLIElement, ListSubheaderProps>((props, ref) => (
  <MuiListSubheader ref={ref} {...props} />
));

ListSubheader.displayName = 'ListSubheader';
